// src/pages/Users/Lexique/components/LexiqueRow.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaVolumeUp, FaCopy, FaHeart, FaRegHeart } from "react-icons/fa";

const collatorFR = new Intl.Collator("fr", { sensitivity: "base" });
const strip = (s = "") =>
  s.toString().normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().trim();

function Highlight({ text, q }) {
  const t = String(text ?? "");
  const query = strip(q || "");
  if (!query) return <>{t}</>;
  const idx = strip(t).indexOf(query);
  if (idx === -1) return <>{t}</>;
  const before = t.slice(0, idx);
  const match  = t.slice(idx, idx + q.length);
  const after  = t.slice(idx + q.length);
  return (
    <>
      {before}
      <mark className="bg-yellow-200 dark:bg-yellow-600/50 rounded px-0.5">{match}</mark>
      {after}
    </>
  );
}

// Détection présence de caractères japonais (kana/kanji)
const hasJPChars = (s = "") => /[\u3040-\u30FF\u4E00-\u9FFF]/.test(s);

// Choix de voix par langue, avec fallback
function pickVoice(langStartsWith = "ja", fallbackLang = "ja-JP") {
  const synth = window.speechSynthesis;
  if (!synth) return { voice: null, lang: fallbackLang };
  const voices = synth.getVoices ? synth.getVoices() : [];
  const v = voices.find((v) => v.lang?.toLowerCase().startsWith(langStartsWith));
  return { voice: v || null, lang: v?.lang || fallbackLang };
}

function speakWith(langStartsWith, text, fallbackLang) {
  try {
    const synth = window.speechSynthesis;
    if (!synth || !text) return;
    const { voice, lang } = pickVoice(langStartsWith, fallbackLang);
    const utt = new SpeechSynthesisUtterance(text);
    if (voice) utt.voice = voice;
    utt.lang = lang;
    utt.rate = 1;   // ajuste si besoin
    utt.pitch = 1;  // ajuste si besoin
    synth.speak(utt);
  } catch {}
}

export default function LexiqueRow({ row, q, isFav, toggleFav }) {
  const jpRaw = row.japonais || "";
  const fr    = row.traduction || "";

  // Conversion romaji -> kana via import dynamique (optionnel)
  const [toKanaFn, setToKanaFn] = useState(null);
  const triedImport = useRef(false);

  useEffect(() => {
    if (triedImport.current) return;
    triedImport.current = true;
    (async () => {
      try {
        // Installe au besoin: npm i wanakana
        const mod = await import("wanakana");
        if (mod?.toKana) setToKanaFn(() => mod.toKana);
      } catch {
        // pas de lib = pas grave
      }
    })();
  }, []);

  // Texte japonais à prononcer : si pas de kana/kanji, on tente toKana(romaji)
  const jpForTTS = useMemo(() => {
    if (!jpRaw) return "";
    if (hasJPChars(jpRaw)) return jpRaw;
    // Nettoyage léger romaji (remplace tirets/underscores par espaces)
    const cleaned = jpRaw.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
    return toKanaFn ? toKanaFn(cleaned) : cleaned;
  }, [jpRaw, toKanaFn]);

  const copyBoth = async () => {
    try {
      await navigator.clipboard.writeText(`${jpRaw} — ${fr}`);
    } catch {}
  };

  return (
    <li className="py-3">
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-start">
        {/* JP */}
        <div className="sm:col-span-5 font-semibold text-slate-900 dark:text-slate-100">
          <Highlight text={jpRaw} q={q} />
        </div>
        {/* FR */}
        <div className="sm:col-span-7 text-slate-700 dark:text-slate-300">
          <i className="not-italic">
            <Highlight text={fr} q={q} />
          </i>
        </div>

        {/* Actions */}
        <div className="sm:col-span-12 flex items-center gap-2 mt-1">
          {/* Prononcer JP */}
          <button
            type="button"
            onClick={() => speakWith("ja", jpForTTS, "ja-JP")}
            title="Prononcer (japonais)"
            className="inline-flex items-center gap-1 text-xs rounded-xl px-2 py-1 ring-1 ring-slate-300
                       text-slate-700 hover:text-slate-900 hover:ring-sky-300 dark:text-slate-300 dark:hover:text-slate-100 dark:ring-slate-700
                       transition"
          >
            <FaVolumeUp /> JP
          </button>

          {/* Prononcer FR */}
          <button
            type="button"
            onClick={() => speakWith("fr", fr, "fr-FR")}
            title="Prononcer (français)"
            className="inline-flex items-center gap-1 text-xs rounded-xl px-2 py-1 ring-1 ring-slate-300
                       text-slate-700 hover:text-slate-900 hover:ring-sky-300 dark:text-slate-300 dark:hover:text-slate-100 dark:ring-slate-700
                       transition"
          >
            <FaVolumeUp /> FR
          </button>

          {/* Copier */}
          <button
            type="button"
            onClick={copyBoth}
            title="Copier la paire (JP — FR)"
            className="inline-flex items-center gap-1 text-xs rounded-xl px-2 py-1 ring-1 ring-slate-300
                       text-slate-700 hover:text-slate-900 hover:ring-sky-300 dark:text-slate-300 dark:hover:text-slate-100 dark:ring-slate-700
                       transition"
          >
            <FaCopy /> Copier
          </button>

          {/* Favori */}
          <button
            type="button"
            onClick={toggleFav}
            title={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
            className={`ml-auto inline-flex items-center gap-1 text-xs rounded-xl px-2 py-1 ring-1 transition
              ${isFav
                ? "ring-red-300 text-red-600 dark:text-red-400 dark:ring-red-800"
                : "ring-slate-300 text-slate-700 dark:text-slate-300 dark:ring-slate-700 hover:ring-sky-300 hover:text-slate-900"
              }`}
          >
            {isFav ? <FaHeart /> : <FaRegHeart />} Favori
          </button>
        </div>
      </div>
    </li>
  );
}
